import { pencilerRoles, urbancanvasRoles, adminRoles } from "../config/config";
import { httpStatusCode } from "../config/constants";
import { User, UserClaims } from "../types/users";

export const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

// This is intended to suppress this warning:
// "The href attribute requires a valid value to be accessible.".
export const blankHref = "#";

export const capitalize = (s: string | undefined) =>
  s ? s.replace(/^./g, s[0].toUpperCase()) : "";

/**
 * Checks if param is undefined, empty or null
 *
 * @param {any} param   - Param to check
 * @return              - True if param fails any of this check. False otherwise
 */
export const doesNotExist = (
  param: any
): param is null | undefined | "" | Record<string, never> | never[] => {
  if (typeof param === "undefined" || param === "" || param === null) return true;
  if (Array.isArray(param) && param.length === 0) return true;
  if (typeof param === "object" && Object.prototype.toString.call(param) !== "[object Date]") {
    return Object.keys(param).length === 0;
  }
  return false;
};

/**
 * Note(gushuro): type predicates don't work here because TS has no way of substracting
 * types from `any`
 *
 * */
export const exists = (param: any) => !doesNotExist(param);

/**
 * Return error string based on Axios failed request response.
 *
 * @param {any} error           - Axios failed response.
 * @return {string}             - Error message.
 */
export const getErrorMessageFromRequest = (error: any) => {
  const { errorText } = getApplicationErrorFromRequest(error);
  return errorText;
};

/**
 * Return error string based on Axios failed request response.
 *
 * @param {any} error                                               - Axios failed response.
 * @return {{errorCode: string, errorText: string}}             - Error message.
 */
export const getApplicationErrorFromRequest = (error: any) => {
  const defaultErrorCode = "unknown";
  if (doesNotExist(error.response)) {
    return { errorCode: defaultErrorCode, errorText: "Server error" };
  }
  if (exists(error.response.data)) {
    const {
      response: {
        data: { errorCode, errorText, message }
      }
    } = error;
    return { errorCode, errorText: exists(errorText) ? errorText : message };
  }
  if (error.response.status === httpStatusCode.error) {
    return { errorCode: defaultErrorCode, errorText: "API endpoint not found" };
  }
  return { errorCode: defaultErrorCode, errorText: "Server not found" };
};

/**
 * Convert number to number with comma separators
 *
 * @param {Number|String} x       - Input number.
 * @returns {string}              - Output number string with commas.
 */
export const getNumberWithCommas = (x: number | string) =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * Selects Progress bar display type based on progress percentage.
 *
 * @param {Number} percentage   - Progress percentage.
 * @returns                     - Progress display type status.
 * @private
 */
export const getProgressDisplayType = (percentage: number) => ({
  success: percentage < 40,
  warning: percentage < 90,
  error: percentage >= 90
});

/**
 * Calculate percentage to process on Progress bar
 *
 * @param {Number} value            - Value
 * @param {Number} total            - Total
 * @param {Boolean} infiniteTotal   - Is total an infinite value?
 * @return {number}                 - Percentage
 * @private
 */
export const getPercentage = (value: number, total: number, infiniteTotal = false) => {
  if (infiniteTotal) return 0;
  if (total === 0) return 100;
  if (value !== 0) return Math.floor((value / total) * 100);
  return 0;
};

export const isAdministrator = (user: UserClaims | Record<string, never>) =>
  exists(
    user.roles?.find(
      role =>
        adminRoles.includes(role.code) &&
        role.organizationId === user.selectedOrganizationId &&
        role.regionId === user.selectedRegionId
    )
  );

export const isModeler = (user: UserClaims | Record<string, never>) =>
  exists(
    user.roles?.find(
      role =>
        role.code.includes("MODELER") &&
        role.organizationId === user.selectedOrganizationId &&
        role.regionId === user.selectedRegionId
    )
  );

export const isPublic = (user: UserClaims | Record<string, never>) => {
  const relevantRoles =
    user.roles?.filter(
      role =>
        role.organizationId === user.selectedOrganizationId &&
        role.regionId === user.selectedRegionId
    ) ?? [];
  return relevantRoles.length === 1 && relevantRoles[0].code.includes("PUBLIC");
};

export const hasRole = (user: UserClaims, role: string) =>
  exists(
    user.roles?.find(
      userRole =>
        userRole.code.includes(role) &&
        //@ts-ignore: role is a string, this will never be ok
        role.organizationId === user.selectedOrganizationId &&
        //@ts-ignore: role is a string, this will never be ok
        role.regionId === user.selectedRegionId
    )
  );

export const usersWithRole = (users: (UserClaims | User)[], role: string) =>
  users.filter(user => hasRole(user as UserClaims, role));

export const canManageUsers = (user: UserClaims | Record<string, never>) =>
  user.roles?.some(
    role =>
      //@ts-ignore
      (exists(pencilerRoles.canOnlyAddUsersTo[role.code]) &&
        //@ts-ignore
        pencilerRoles.canOnlyAddUsersTo[role.code].length > 0) ||
      //@ts-ignore
      (exists(urbancanvasRoles.canOnlyAddUsersTo[role.code]) &&
        //@ts-ignore
        urbancanvasRoles.canOnlyAddUsersTo[role.code].length > 0)
  );

//Unused
export const memoize = (fn: any) => {
  let lastArg: any;
  let lastResult: any;
  return (arg: any) => {
    if (arg !== lastArg) {
      lastArg = arg;
      lastResult = fn(arg);
    }
    return lastResult;
  };
};
